import { INDUSTRIAL_CASE_SOLUTIONS } from '@/domain/industrialCase/industrialCaseDocument';

/**
 * ### INDUSTRIAL_CASE_BASE_PATH
 *
 * Where published customer cases live, without a locale prefix.
 *
 * Every case URL in the app is built from this one constant, so moving cases to
 * a different address is a change here rather than a search across the codebase.
 * The locale prefix is added by the link and SEO helpers, not here.
 *
 * Cases sit at the site root rather than under a solution: one case list covers
 * every solution, and the `?solution=` tab decides which slice is shown.
 */
export const INDUSTRIAL_CASE_BASE_PATH = '/cases';

/**
 * ### INDUSTRIAL_CASE_LIST_SOLUTIONS
 *
 * Views available on the customer-case list. `all` keeps the list useful as a
 * single entry point while product-specific tabs preserve filtered views.
 */
export const INDUSTRIAL_CASE_LIST_SOLUTIONS = [
  'all',
  ...INDUSTRIAL_CASE_SOLUTIONS,
] as const;

/**
 * ### IndustrialCaseListSolution
 *
 * One customer-case list view, including the unfiltered view.
 */
export type IndustrialCaseListSolution =
  (typeof INDUSTRIAL_CASE_LIST_SOLUTIONS)[number];

/**
 * ### buildIndustrialCasePath
 *
 * Builds the path of one published case.
 *
 * @param slug The document's slug, which is the URL segment
 * @returns The unlocalized case path
 */
export function buildIndustrialCasePath(slug: string) {
  return `${INDUSTRIAL_CASE_BASE_PATH}/${slug}`;
}

/**
 * ### buildIndustrialCaseListPath
 *
 * Builds the path of the case list, optionally filtered to one solution.
 *
 * The filter is a query parameter rather than a segment so the list keeps one
 * canonical address, and so a tab can be linked to without creating a second
 * page for search engines to index.
 *
 * @param solution Solution tab to open. Omit for the default tab.
 * @returns The unlocalized list path
 */
export function buildIndustrialCaseListPath(
  solution?: IndustrialCaseListSolution,
) {
  return solution
    ? `${INDUSTRIAL_CASE_BASE_PATH}?solution=${solution}`
    : INDUSTRIAL_CASE_BASE_PATH;
}

/** Suffix that turns a case address into its Markdown endpoint. */
const MARKDOWN_SUFFIX = '.md';

/**
 * ### INDUSTRIAL_CASE_MARKDOWN_REWRITE_PREFIX
 *
 * Internal address the per-case Markdown endpoint is served from.
 *
 * A Pages Router file cannot own a route whose last segment is `{slug}.md`, so
 * the endpoint lives in its own tree and `next.config.js` rewrites
 * `/cases/{slug}.md` onto it. Nothing ever links here; the constant exists so
 * the SEO helpers can keep the tree out of canonical and alternate URLs, and
 * so that the rewrite target moves with the base path.
 */
export const INDUSTRIAL_CASE_MARKDOWN_REWRITE_PREFIX = `${INDUSTRIAL_CASE_BASE_PATH}-md`;

/**
 * ### INDUSTRIAL_CASE_LIST_MARKDOWN_PATH
 *
 * Markdown endpoint listing every published case, which is the list path with a
 * `.md` suffix.
 */
export const INDUSTRIAL_CASE_LIST_MARKDOWN_PATH = `${INDUSTRIAL_CASE_BASE_PATH}${MARKDOWN_SUFFIX}`;

/**
 * ### buildIndustrialCaseMarkdownPath
 *
 * Builds the Markdown endpoint of one published case, which is the case path
 * with a `.md` suffix.
 *
 * @param slug The document's slug
 * @returns The unlocalized Markdown path
 */
export function buildIndustrialCaseMarkdownPath(slug: string) {
  return `${buildIndustrialCasePath(slug)}${MARKDOWN_SUFFIX}`;
}

/**
 * The one path segment sitting directly under the case base path, or
 * `undefined` when the path addresses something else. Anything nested deeper
 * is rejected, because no case address has a second segment.
 */
function readCaseSegment(path: string): string | undefined {
  if (!path.startsWith(`${INDUSTRIAL_CASE_BASE_PATH}/`)) return undefined;

  const segment = path.slice(INDUSTRIAL_CASE_BASE_PATH.length + 1);

  return segment.length > 0 && !segment.includes('/') ? segment : undefined;
}

/**
 * ### isIndustrialCaseDetailPath
 *
 * Reports whether an unlocalized path addresses one case's HTML page, as
 * opposed to its Markdown endpoint.
 *
 * Kept beside the builders so the shape a request is matched against and the
 * shape a link is written with can never drift apart. The slug itself is not
 * validated here; a slug that names no case is the page's own 404.
 *
 * @param path Unlocalized path to test, as it appears in the request URL
 * @returns Whether the path is a case detail page
 */
export function isIndustrialCaseDetailPath(path: string) {
  const segment = readCaseSegment(path);

  return segment !== undefined && !segment.endsWith(MARKDOWN_SUFFIX);
}

/**
 * ### isIndustrialCaseMarkdownPath
 *
 * Reports whether an unlocalized path addresses one case's Markdown endpoint.
 *
 * Kept beside the builders for the same reason as
 * {@link isIndustrialCaseDetailPath}, and deliberately disjoint from it: the
 * two decide which router serves a request, so a path must never be both.
 *
 * @param path Unlocalized path to test, as it appears in the request URL
 * @returns Whether the path is a case Markdown endpoint
 */
export function isIndustrialCaseMarkdownPath(path: string) {
  const segment = readCaseSegment(path);

  return (
    segment !== undefined &&
    segment.length > MARKDOWN_SUFFIX.length &&
    segment.endsWith(MARKDOWN_SUFFIX)
  );
}
